import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { acceptLinkProjectShare, getLinkProjectShare, type ProjectShare } from '../lib/agent-project-shares-api';
import './ProjectShare.css';
import { listAgentProjects, type AgentProject } from '../lib/agent-projects-api';

export default function ProjectShareClaimPage() {
  const location = useLocation();
  const [token] = useState(() => {
    const encoded = location.hash.replace(/^#/, '');
    if (!encoded) return '';
    try { return decodeURIComponent(encoded); } catch { return ''; }
  });
  const navigate = useNavigate();
  const [share, setShare] = useState<ProjectShare | null>(null);
  const [notice, setNotice] = useState('正在加载项目分享…');
  const [projects, setProjects] = useState<AgentProject[]>([]);
  const [replacement, setReplacement] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setNotice('该分享链接无效、已过期或已被撤销。');
      return () => { cancelled = true; };
    }
    void getLinkProjectShare(token)
      .then((item) => {
        if (!cancelled) {
          setShare(item);
          setNotice('');
        }
      })
      .catch(() => {
        if (!cancelled) setNotice('该分享链接无效、已过期或已被撤销。');
      });
    return () => { cancelled = true; };
  }, [refreshVersion, token]);

  const claim = async (replaceProjectId?: string) => {
    setBusy(true);
    try {
      const result = await acceptLinkProjectShare(
        token,
        replaceProjectId,
        projects.map((project) => project.id),
      );
      navigate(
        `/chat?agent_profile=${encodeURIComponent(result.share.agentId)}&active_project_id=${encodeURIComponent(result.importedProjectId)}`,
        { replace: true },
      );
    } catch (error) {
      if ((error as { status?: number }).status === 409 && share) {
        try {
          const result = await listAgentProjects(share.agentId);
          setProjects(result.items);
          setNotice('项目槽位已满，请选择一个项目移入 30 天回收站后再领取。');
        } catch {
          setNotice('项目槽位已变化，请刷新后重试。');
        }
      } else {
        setNotice('领取失败，请重试。');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="project-share-claim-page">
      <section className="project-share-claim-card">
        <h1>领取项目记忆</h1>
        {notice ? <p role="status">{notice}</p> : null}
        {share && !share.viewerAuthenticated ? (
          <>
            <h2>{share.projectName || '项目分享'}</h2>
            <p>请先登录，再领取这个项目记忆。登录或开通智能体后，请返回本页重新检查。</p>
            <a
              href={`/chat?workspace=houmao&agent_profile=${encodeURIComponent(share.agentId)}`}
              target="_blank"
              rel="noreferrer"
            >
              前往登录或开通智能体
            </a>
            <button type="button" onClick={() => setRefreshVersion((value) => value + 1)}>
              我已完成，重新检查
            </button>
          </>
        ) : null}
        {share?.viewerAuthenticated && !share.hasAgentAccess ? (
          <>
            <h2>{share.projectName || '项目分享'}</h2>
            <p>当前账号尚未开通该智能体。为保护分享内容，暂不展示项目摘要和记忆详情。</p>
            <a
              href={`/chat?workspace=houmao&agent_profile=${encodeURIComponent(share.agentId)}`}
              target="_blank"
              rel="noreferrer"
            >
              前往开通智能体
            </a>
            <button type="button" onClick={() => setRefreshVersion((value) => value + 1)}>
              我已开通，重新检查
            </button>
          </>
        ) : null}
        {share?.viewerAuthenticated && share.hasAgentAccess ? (
          <>
            <h2>{share.projectName}</h2>
            <p>{share.preview.summary || '暂无项目摘要'}</p>
            <p>包含 {share.preview.activeEntryCount} 条活跃记忆</p>
            {projects.length ? (
              <>
                <label>
                  选择要移入回收站的项目
                  <select
                    aria-label="替换现有项目"
                    value={replacement}
                    onChange={(event) => setReplacement(event.target.value)}
                  >
                    <option value="">请选择项目</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>{project.projectName}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                  />
                  我确认将所选项目软删除并保留在回收站 30 天
                </label>
                <button
                  type="button"
                  disabled={busy || !replacement || !confirmed}
                  onClick={() => void claim(replacement)}
                >
                  确认替换并领取
                </button>
              </>
            ) : (
              <button type="button" disabled={busy} onClick={() => void claim()}>
                {busy ? '正在领取…' : '领取并打开项目'}
              </button>
            )}
          </>
        ) : null}
      </section>
    </main>
  );
}
